<script lang="ts" setup>
import { useMenu } from '~/api/drupal/useMenu'
import { MediaImageFragment } from '~/api/graphql/generated'
import { Link } from '~/types'
import { useI18n } from 'vue-i18n'

const { menu: menuCompliance } = await useMenu('compliance-menu')
const { t } = useI18n()

const props = defineProps<{
  logo: MediaImageFragment
  address?: string
  schedule?: string
  cta?: Link
  links?: Link[]
}>()

const menuLinks = computed(() => {
  return (
    props.links?.map((link, index) => ({
      id: index,
      link: {
        text: link.text,
        url: link.url,
      },
    })) || []
  )
})
</script>

<template>
  <footer class="footer box">
    <div class="container">
      <div class="top">
        <div class="logo">
          <Image v-if="logo" :entity="logo" type="teaser-main-menu" />
        </div>
        <FooterSocial />
      </div>

      <div class="middle">
        <div class="grid">
          <div class="tl-3 address">
            <div v-if="address" v-html="address" />
            <Button v-if="cta" primary slide :to="cta.url">
              {{ cta.text }}
            </Button>
          </div>
          <div class="tl-4 schedule">
            <div v-if="schedule" v-html="schedule" />
          </div>
          <div class="tl-3 tl-o-2">
            <LinkList :items="menuLinks" />
          </div>
        </div>
      </div>

      <div class="bottom">
        <span class="copyright text-small">{{ t('footer.copyright', { year: new Date().getFullYear() }) }}</span>
        <FooterComplianceMenu class="menu text-small" :menu="menuCompliance" />
        <LanguageSwitcher class="switcher" />
      </div>
    </div>
  </footer>
</template>
<style lang="scss" scoped>
.footer {
  padding-block: 80px;
  background-color: var(--c-background-medium);
}
.top {
  margin-bottom: 48px;
  @include breakpoint(ts) {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .logo {
    max-width: 140px;
    margin-bottom: 24px;
    @include breakpoint(ts) {
      margin-bottom: 0;
    }
  }
  @include breakpoint(ts) {
    :deep(.social) {
      display: flex;
      align-items: center;
      h5 {
        margin-bottom: 0;
        margin-right: 24px;
      }
    }
  }
}
.middle {
  .address {
    margin-bottom: 48px;
    @include breakpoint(tl) {
      margin-bottom: 0;
    }
    :deep(p) {
      color: var(--c-primary);
      @include f-18;
    }
  }
  .schedule {
    :deep(table) {
      border: none;
      width: 100% !important;
      td,
      th {
        width: auto !important;
        border: none;
        padding: 0 0 24px 24px;
        &:first-child {
          padding-left: 0;
        }
        p {
          margin-bottom: 0;
          @include f-18;
        }
        strong {
          color: var(--c-primary);
        }
      }
    }
  }
}
.bottom {
  margin-top: 120px;
  display: grid;
  grid-template-columns: 100%;
  gap: 32px;
  align-items: center;
  @include breakpoint(t) {
    grid-template-columns: 1fr auto;
  }
  @include breakpoint('ds') {
    margin-top: 174px;
    grid-template-columns: auto 1fr auto;
    gap: 48px;
  }
}

.copyright {
  grid-row: 3;
  @include breakpoint(t) {
    grid-row: 2;
  }
  @include breakpoint(ds) {
    grid-row: 1;
  }
}

.menu {
  grid-row: 2;
  @include breakpoint(t) {
    grid-row: auto;
  }
  @include breakpoint(ds) {
    margin-left: auto;
  }
}

.switcher {
  justify-self: start;
  @include breakpoint(t) {
    justify-self: end;
  }
}
</style>
